import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, FlexBox } from '@sternum/styled-system';
import { Avatar, getPersonInitials, Text12B } from '@sternum/ui';
import { Icon, useConfirmModal } from '@sternum/web/components';
import { AvatarWithBadge } from '@sternum/web/components/AvatarWithBadge';
import { TextOverflowBox } from '@sternum/web/components/TextOverflowBox';
import { Colors } from '@sternum/web/components/theme/constants';
import { MainThemeProvider } from '@sternum/web/components/theme/main';
import { Config } from '@sternum/web/config';
import { routes } from '@sternum/web/config/routes';
import { useMenu } from '@sternum/web/features/appContainerWithMenu/hooks';
import {
  useCheckPermission,
  useGeneratePathWithCurrentClient,
  useLogout,
  useMe,
} from '@sternum/web/features/auth/hooks';
import { AllPermissionActions } from '@sternum/web/features/auth/models';
import { SpinnerIcon } from '@sternum/web/icons';
import { namespaces } from '@sternum/web/services/i18n';

import { WorkspacesSection } from './WorkspacesSection';

import {
  AvatarText,
  ClientNameText,
  ContainerFull,
  ContainerSmall,
  FullNameText,
  LinkStyled,
  MenuDivider,
  MenuItem,
  PopupContentsContainer,
  PopupStyled,
  StatusDot,
  StatusText,
} from './UserPopup.styles';

export function UserPopup() {
  const { t } = useTranslation(namespaces.appContainerWithMenu);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const { currentUser, currentClient } = useMe();
  const { can } = useCheckPermission();
  const { isFullMenuVisible } = useMenu();
  const generatePath = useGeneratePathWithCurrentClient();
  const { logout, isLoading: isLoggingOut } = useLogout({ setLastLocationPath: false });

  const [popupOpen, setPopupOpen] = useState(false);
  const [LogoutConfirmationModal, showLogoutConfirmationModal] = useConfirmModal();

  const canShowDocumentationCenter = can(AllPermissionActions.DocumentationCenterLink);
  const canShowUserManagement = can(AllPermissionActions.UserRead);
  const canShowWorkspacesSection = can(AllPermissionActions.ClientsRead);

  const handleLogout = async () => {
    const isConfirmed = await showLogoutConfirmationModal();

    if (!isConfirmed) return;

    await logout();
  };

  const renderFullMenuView = () => (
    <ContainerFull>
      <FlexBox ref={buttonRef} alignItems="center" onClick={() => setPopupOpen((x) => !x)}>
        <AvatarWithBadge size={34} color={Colors.BlueDark}>
          <Text12B color={Colors.White}>{getPersonInitials(currentUser?.fullName || '')}</Text12B>
        </AvatarWithBadge>
        <ClientNameText>
          <TextOverflowBox tooltipContent={currentClient?.name}>{currentClient?.name}</TextOverflowBox>
        </ClientNameText>
        <Box display="inline-flex" ml={4} transform={popupOpen ? 'rotate(180deg)' : 'none'}>
          <Icon type="ChevronDownIcon" color={{ Dark: 'GreyDarker', Light: 'GreyDarker' }} />
        </Box>
      </FlexBox>
    </ContainerFull>
  );

  const renderCollapsedMenuView = () => (
    <ContainerSmall>
      <FlexBox ref={buttonRef} alignItems="center" onClick={() => setPopupOpen((x) => !x)}>
        <AvatarWithBadge aria-label="user popup avatar" size={34} color={Colors.BlueDark}>
          <Text12B color={Colors.White}>{getPersonInitials(currentUser?.fullName || '')}</Text12B>
        </AvatarWithBadge>
      </FlexBox>
    </ContainerSmall>
  );

  const urlToProfilePage = generatePath(routes.user.profile.absolute);

  return (
    <>
      {isFullMenuVisible ? renderFullMenuView() : renderCollapsedMenuView()}

      <PopupStyled
        position="top-start"
        trackedElementRef={buttonRef}
        show={popupOpen}
        onClickAway={() => setPopupOpen(false)}
        offsetY={-10}
      >
        <PopupContentsContainer aria-label="user popup content">
          <LinkStyled to={urlToProfilePage}>
            <MenuItem $smallSpacing>
              <FlexBox>
                <Box mr={canShowWorkspacesSection ? 12 : 26}>
                  <AvatarWithBadge
                    size={40}
                    color={Colors.BlueDark}
                    badge={
                      !canShowWorkspacesSection && (
                        <AvatarWithBadge.Badge position={{ bottom: '0px', right: '-15px' }}>
                          <Avatar size={26}>
                            <AvatarText>{currentClient?.name?.[0] || '?'}</AvatarText>
                          </Avatar>
                        </AvatarWithBadge.Badge>
                      )
                    }
                  >
                    <AvatarText>{getPersonInitials(currentUser?.fullName || '?')}</AvatarText>
                  </AvatarWithBadge>
                </Box>

                <Box>
                  <FullNameText>{currentUser?.fullName}</FullNameText>
                  <FlexBox alignItems="center" gap={6}>
                    <StatusDot $status="active" />
                    <StatusText>{t('statusActive')}</StatusText>
                  </FlexBox>
                </Box>
              </FlexBox>

              <Box display="inline-flex">
                <Icon type="ChevronRightIcon" color={{ Dark: 'GreyDarker', Light: 'GreyDarker' }} />
              </Box>
            </MenuItem>
          </LinkStyled>

          <MenuDivider />

          {canShowWorkspacesSection && (
            <>
              <WorkspacesSection />
              <MenuDivider />
            </>
          )}

          {canShowDocumentationCenter && (
            <>
              <LinkStyled to={Config.app.documentationCenterLink} target={'_blank'}>
                <MenuItem>{t('documentationCenter')}</MenuItem>
              </LinkStyled>
              <MenuDivider />
            </>
          )}

          {canShowUserManagement && (
            <>
              <LinkStyled to={generatePath(routes.user.manageUsers.absolute)}>
                <MenuItem>{t('manageUsers')}</MenuItem>
              </LinkStyled>
              <MenuDivider />
            </>
          )}

          <MenuItem>
            <LinkStyled to={Config.app.supportLink} target="_blank">
              {t('supportAndFeedback')}
            </LinkStyled>
          </MenuItem>

          <MenuDivider />

          <MenuItem onClick={handleLogout}>{t('logOut')}</MenuItem>
        </PopupContentsContainer>
      </PopupStyled>
      <MainThemeProvider>
        <LogoutConfirmationModal
          isCentered
          aria-label="logout confirmation modal"
          title={t('logOut')}
          description={t('logOutConfirmation')}
          confirmLabel={isLoggingOut ? <SpinnerIcon color2={Colors.GreyDarkerMiddle} /> : t('logOut')}
          cancelButtonDisabled={isLoggingOut}
          confirmButtonDisabled={isLoggingOut}
          confirmButtonVariant="error-primary"
        />
      </MainThemeProvider>
    </>
  );
}
